/**
 * 航班运行事务邮件相关API
 * 处理许可申请、燃油申请、配餐订单等事务邮件的发送
 */

import apiClient from "./axios";

/**
 * 统一邮件API调用函数
 * 直接调用后端的统一邮件服务
 *
 * 注意：后端会根据 is_bulk_split 参数自动决定使用同步还是异步执行模式
 */
export const sendUniversalEmail = async (emailData) => {
    try {
        const response = await apiClient.post("/v1/mail/universal/create-and-execute", emailData);
        return response.data;
    } catch (error) {
        console.error("发送统一邮件失败:", error);
        throw error;
    }
};

/**
 * 发送批量许可申请邮件
 * @param {Object} emailData - 邮件数据
 * @param {Array} emailData.flight_operation_ids - 航班运行ID数组
 * @param {string} emailData.transaction_type - 事务类型
 * @param {Object} emailData.email_content - 邮件内容
 * @returns {Promise} API响应
 */
export const sendBatchPermitRequestEmail = async (emailData) => {
    const { flight_operation_ids, transaction_type, email_content } = emailData;

    // 构建业务关联信息
    // 修正：record_type 应该反映实际存储的记录类型，而不是业务分类
    // linked_record_id 将存储航班ID，所以 record_type 应该是 "FLIGHT_OPERATION"
    // link_type 用于区分具体的业务场景
    const businessLink = {
        record_id: 0, // 批量操作使用占位符，实际ID在 details.flight_operation_ids 中
        record_type: "FLIGHT_OPERATION", // 反映实际存储的记录类型
        link_type: `${transaction_type}_APPLICATION_BATCH`, // 业务场景分类
        details: {
            flight_operation_ids,
            transaction_type,
            batch_size: flight_operation_ids.length,
        },
    };

    // 构建统一邮件请求数据
    const requestData = {
        business_link: businessLink,
        recipients: email_content.recipients,
        subject: email_content.subject,
        body_html: email_content.body_html,
        body_text: email_content.body_text || null,
        attachments: email_content.attachments || [],
        sender_user_id: email_content.sender_user_id || 1,
        user_email_config_id: email_content.user_email_config_id || null,
        send_interval: 1.0,
        is_bulk_split: email_content.recipients.length > 1,
        content_block_context: email_content.content_block_context || {},
    };

    return await sendUniversalEmail(requestData);
};

/**
 * 发送批量燃油申请邮件
 * @param {Object} emailData - 邮件数据
 * @returns {Promise} API响应
 */
export const sendBatchFuelRequestEmail = async (emailData) => {
    return await sendBatchPermitRequestEmail({
        ...emailData,
        transaction_type: "FUEL_REQUEST",
    });
};

/**
 * 发送批量配餐订单邮件
 * @param {Object} emailData - 邮件数据
 * @returns {Promise} API响应
 */
export const sendBatchCateringOrderEmail = async (emailData) => {
    return await sendBatchPermitRequestEmail({
        ...emailData,
        transaction_type: "CATERING_ORDER",
    });
};

/**
 * 发送批量飞行简报邮件
 * @param {Object} emailData - 邮件数据
 * @returns {Promise} API响应
 */
export const sendBatchFlightBriefEmail = async (emailData) => {
    return await sendBatchPermitRequestEmail({
        ...emailData,
        transaction_type: "FLIGHT_BRIEF",
    });
};

/**
 * 发送批量机组接送邮件
 * @param {Object} emailData - 邮件数据
 * @returns {Promise} API响应
 */
export const sendBatchCrewTransportationEmail = async (emailData) => {
    return await sendBatchPermitRequestEmail({
        ...emailData,
        transaction_type: "CREW_TRANSPORTATION",
    });
};

/**
 * 发送批量酒店住宿邮件
 * @param {Object} emailData - 邮件数据
 * @returns {Promise} API响应
 */
export const sendBatchHotacEmail = async (emailData) => {
    return await sendBatchPermitRequestEmail({
        ...emailData,
        transaction_type: "HOTAC",
    });
};

export default {
    sendUniversalEmail,
    sendBatchPermitRequestEmail,
    sendBatchFuelRequestEmail,
    sendBatchCateringOrderEmail,
    sendBatchFlightBriefEmail,
    sendBatchCrewTransportationEmail,
    sendBatchHotacEmail,
};
